import { DatePicker, Empty } from 'antd';
import dayjs from 'dayjs';
import { FC, useMemo, useState } from 'react';

import { formatDate, getDefaultDates } from 'src/utils/date-util';

import { useIssueStoreManagementData } from 'src/hooks/subscription/useIssueStoreManagementData';

import StoreSearch from 'src/atomic-components/molecules/StoreSearch/StoreSearch';

import IssueStoreSummaryCard from './IssueStoreManagement/IssueStoreManagementSummary';
import IssueStoreManagementTable from './IssueStoreManagement/IssueStoreManagementTable';

const { RangePicker } = DatePicker;

// 기본값: 30일 전 ~ 3일 후
const defaultDates = getDefaultDates(30, 3);

/**
 * 3일 후까지 선택 가능
 */
const disableFutureDate = (current: dayjs.Dayjs) => {
  return current && current > dayjs().add(3, 'day').endOf('day');
};

const IssueStoreManagement: FC = () => {
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const [selectedStoreName, setSelectedStoreName] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>(defaultDates);

  const startDate = useMemo(() => (dateRange[0] ? formatDate(dateRange[0].toDate(), 'yyyy-MM-dd') : ''), [dateRange]);
  const endDate = useMemo(() => (dateRange[1] ? formatDate(dateRange[1].toDate(), 'yyyy-MM-dd') : ''), [dateRange]);

  const { rowData, summary, isLoading } = useIssueStoreManagementData({
    storeId: selectedStoreId,
    startDate,
    endDate,
  });

  const handleStoreSelect = (storeId: string | null, storeName: string) => {
    setSelectedStoreId(storeId);
    setSelectedStoreName(storeName);
  };

  const handleDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    if (dates) {
      setDateRange(dates);
    }
  };

  return (
    <div className='height100 flexColumn' style={{ gap: 16 }}>
      {/* 상단 필터 */}
      <div className='flexRow' style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div className='flexRow' style={{ alignItems: 'center', gap: 8, minWidth: 300 }}>
          <StoreSearch selectedStoreId={selectedStoreId} onStoreSelect={handleStoreSelect} />
        </div>
        <div className='flexRow' style={{ alignItems: 'center', gap: 8 }}>
          <span>조회 기간:</span>
          <RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            disabledDate={disableFutureDate}
            allowClear={false}
          />
        </div>
      </div>

      {/* 매장 미선택 시 안내 */}
      {!selectedStoreId ? (
        <div className='height100 flexColumn flexJustifyCenter flexAlignCenter'>
          <Empty description='매장을 선택해주세요' />
        </div>
      ) : (
        <>
          {/* 대시보드 요약 */}
          <IssueStoreSummaryCard summary={summary} isLoading={isLoading} storeName={selectedStoreName ?? undefined} />

          {/* 테이블 */}
          <div style={{ flex: 1, minHeight: 0 }}>
            <IssueStoreManagementTable rowData={rowData} isLoading={isLoading} />
          </div>
        </>
      )}
    </div>
  );
};

export default IssueStoreManagement;
